import { useMemo, type MouseEvent as ReactMouseEvent } from "react";
import { RichTreeView } from "@mui/x-tree-view/RichTreeView";
import { useTreeViewApiRef } from "@mui/x-tree-view/hooks";
import { toTreeItems, withAncestorKeys, pruneOrphanKeys, expandCheckedClosure, type AntdTreeNode } from "./treeData";

export type { AntdTreeNode };

// Community-tier replacement for antd <Tree>. Maps antd treeData onto MUI
// RichTreeView. `checkable` enables checkbox multi-select (antd parity).

export interface TreeProps {
  treeData: AntdTreeNode[];
  checkable?: boolean;
  multiSelect?: boolean;
  defaultExpandedKeys?: string[];
  selectedKeys?: string[];
  onSelect?: (keys: string[]) => void;
  /** antd checkbox API: checkedKeys + onCheck (used with `checkable`).
   *  onCheck param is loose because antd passes Key[] or a {checked} object. */
  checkedKeys?: string[];
  onCheck?: (keys: any) => void; // eslint-disable-line @typescript-eslint/no-explicit-any
  expandedKeys?: string[];
  autoExpandParent?: boolean;
  onExpand?: (keys: any) => void; // eslint-disable-line @typescript-eslint/no-explicit-any
  height?: number;
  style?: import("react").CSSProperties;
  className?: string;
}

export const Tree = ({
  treeData,
  checkable,
  multiSelect,
  defaultExpandedKeys,
  selectedKeys,
  onSelect,
  checkedKeys,
  onCheck,
  expandedKeys,
  autoExpandParent,
  onExpand,
  height,
  style,
  className,
}: TreeProps) => {
  const apiRef = useTreeViewApiRef();
  const items = useMemo(() => toTreeItems(treeData), [treeData]);
  const multi = multiSelect ?? checkable ?? false;
  // With `checkable`, antd's checkedKeys/onCheck drive selection; otherwise selectedKeys/onSelect.
  const selected = checkable ? checkedKeys : selectedKeys;
  const emit = checkable ? onCheck : onSelect;
  // Make the controlled expanded set ancestor-closed so MUI renders like antd:
  //  - autoExpandParent: fold ancestors IN (expanding a deep node shows its path)
  //  - otherwise: prune orphaned keys OUT (a collapsed parent hides its subtree)
  const expandedItems = useMemo(() => {
    if (!expandedKeys) return expandedKeys;
    return autoExpandParent
      ? withAncestorKeys(treeData, expandedKeys)
      : pruneOrphanKeys(treeData, expandedKeys);
  }, [treeData, expandedKeys, autoExpandParent]);

  // Consumers store a minimal/compressed checked set (just the top fully-checked
  // node). MUI checks a box only if its own id is in selectedItems, so expand to
  // the full descendant closure or every child renders unchecked.
  const selectedItems = useMemo(
    () => (checkable && selected ? expandCheckedClosure(treeData, selected) : selected),
    [checkable, treeData, selected],
  );

  return (
    <RichTreeView
      apiRef={apiRef}
      items={items}
      checkboxSelection={checkable}
      multiSelect={multi}
      // Drive selection from the whole row (label AND checkbox) via setItemSelection.
      // MUI's built-in checkbox toggle doesn't fire selection in this controlled +
      // selectionPropagation setup (clicking the box did nothing), and antd toggled on
      // a click anywhere on the row anyway. preventDefault stops the native checkbox
      // from also toggling so we never double-fire; only the expand/collapse arrow is
      // left to MUI.
      {...(checkable
        ? {
            onItemClick: (event: ReactMouseEvent, itemId: string) => {
              const target = event.target as HTMLElement;
              if (target.closest(".MuiTreeItem-iconContainer")) return; // expand arrow
              event.preventDefault();
              apiRef.current?.setItemSelection({
                itemId,
                event,
                keepExistingSelection: true,
                shouldBeSelected: !selectedItems?.includes(itemId),
              });
            },
          }
        : {})}
      // antd's checkable Tree cascades a parent check to all descendants and marks a
      // parent indeterminate for a partial child selection. MUI X v8 does this
      // natively via selectionPropagation (the old manual onChange approach could
      // never render the indeterminate parent, and left children unchecked).
      {...(checkable ? { selectionPropagation: { descendants: true, parents: true } } : {})}
      defaultExpandedItems={defaultExpandedKeys}
      {...(expandedItems ? { expandedItems } : {})}
      {...(selectedItems ? { selectedItems } : {})}
      onSelectedItemsChange={(_event, ids) => {
        const next = Array.isArray(ids) ? ids : ids ? [ids] : [];
        // With selectionPropagation MUI already emits the full cascaded set.
        emit?.(next);
      }}
      onExpandedItemsChange={onExpand ? (_e, ids) => onExpand(ids) : undefined}
      style={{ ...(height ? { maxHeight: height, overflow: "auto" } : {}), ...style }}
      className={className}
    />
  );
};
